import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pagination } from 'antd';
import MarketingLayout from '../MarketingLayout';
import { listPublicResourceItems } from '../../../services/content';
import { ContentItem } from '../../../types/content';
import { routeSlugToChannelKey, RESOURCE_CHANNELS } from '../../../config/resourceHub';
import { useDocumentMeta } from '../../../hooks/useDocumentMeta';
import ContentThumbnail from '../../../components/ContentThumbnail';

const CONTENT_TYPE_LABEL: Record<string, string> = { article: '文章', photo_set: '图集', video: '视频' };

const ResourceChannelListPage: React.FC = () => {
  const { channelSlug } = useParams<{ channelSlug: string }>();
  const channelKey = routeSlugToChannelKey(channelSlug || '');
  const channelConfig = RESOURCE_CHANNELS.find((c) => c.routeSlug === channelSlug);

  const [rows, setRows] = useState<ContentItem[]>([]);
  const [channelName, setChannelName] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [channelKey]);

  useEffect(() => {
    if (!channelKey) {
      setRows([]);
      setTotal(0);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const data = await listPublicResourceItems(channelKey, { page, page_size: pageSize });
        setRows(data.results || []);
        setTotal(data.count || 0);
        if (data.channel?.name) setChannelName(data.channel.name);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [channelKey, page, pageSize]);

  useDocumentMeta({
    title: channelConfig?.label || channelName,
    description: `安飞供应链资源中心 · ${channelConfig?.label || channelName}`,
  });

  if (!channelConfig) {
    return (
      <MarketingLayout>
        <section className="mx-auto max-w-6xl px-6 py-14">
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-500 shadow-sm">板块不存在</div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 py-14">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <Link to="/resources" className="hover:text-blue-600 hover:underline">
            资源中心
          </Link>
          <span>/</span>
          <span className="text-gray-700">{channelConfig.label}</span>
        </div>
        <h1 className="text-2xl font-normal text-gray-900">{channelConfig.label}</h1>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        {loading ? (
          <div className="py-16 text-center text-sm text-gray-500">加载中...</div>
        ) : rows.length ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((item) => (
                <Link
                  key={item.id}
                  to={`/resources/${channelSlug}/${item.slug}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-[#d9eaff] bg-white shadow-sm transition hover:shadow-md"
                >
                  <div className="aspect-[16/10] w-full overflow-hidden bg-gray-100">
                    <ContentThumbnail
                      imageUrl={item.image_url}
                      videoUrlYoutube={item.video_url_youtube}
                      contentType={item.content_type}
                      title={item.title}
                      className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-4">
                    <div className="flex items-center gap-2">
                      {item.content_type && item.content_type !== 'article' ? (
                        <span className="rounded bg-[#eef6ff] px-2 py-0.5 text-xs font-medium text-[#2f82e8]">
                          {CONTENT_TYPE_LABEL[item.content_type]}
                        </span>
                      ) : null}
                      {item.is_original === false ? (
                        <span className="rounded bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">转载</span>
                      ) : null}
                    </div>
                    <h3 className="line-clamp-2 text-base font-semibold text-gray-900 group-hover:text-[#2f82e8]">
                      {item.title}
                    </h3>
                    {item.summary ? <p className="line-clamp-2 text-sm text-gray-500">{item.summary}</p> : null}
                    <span className="mt-auto text-xs text-gray-400">
                      {(item.published_at || item.created_at || '').replace('T', ' ').slice(0, 10)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Pagination current={page} pageSize={pageSize} total={total} onChange={setPage} showSizeChanger={false} />
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 shadow-sm">
            暂无内容，敬请期待
          </div>
        )}
      </section>
    </MarketingLayout>
  );
};

export default ResourceChannelListPage;
